<template>
  <Layout
    :errors="errors"
    :title="trans(`models.${resourceType}.content.edit.action`)"
    :toasts="toasts"
    :resource="model"
  >
    <template #top>
      <resource-action-header :model="model" :action="'edit'"></resource-action-header>
    </template>
    <ul class="nav nav-tabs gap-1" style="margin: 0 0 -1px 0.5rem">
      <li class="nav-item">
        <button :class="focus === 'self' ? 'active' : 'text-muted'" class="nav-link" @click="setFocus('self')">
          {{ trans(`models.${resourceType}.name.singular`) }}
        </button>
      </li>
      <template v-if="model.data.id">
        <template v-for="(collection, name) in model.data.collections" :key="name">
          <li v-if="meta.editable.includes(name)" class="nav-item">
            <button :class="focus === name ? 'active' : 'text-muted'" class="nav-link" @click="setFocus(name)">
              {{ trans(`models.${resourceType}.relations.${name}`) }}
            </button>
          </li>
        </template>
      </template>
    </ul>
    <div v-if="model.data && focus === 'self'" class="card rounded-corner">
      <div class="card-body">
        <resource-form :errors="errors" :resource="model" @update:toasts="addToasts($event)"></resource-form>
      </div>
    </div>
    <div v-if="model.data.id">
      <div v-for="(collection, name) in model.data.collections" :key="name">
        <template v-if="meta.editable.includes(name) && focus == name">
          <card
            :id="`resource-component_${resourceType}-${name}_list`"
            :title="trans('models.' + resourceType + '.relations.' + name)"
            class="position-relative"
          >
            <resource-collection
              :collection-type="model.data.collections[name].meta.config.component?.type ?? 'list'"
              :mode="'dashboard-component'"
              :models="model.data.collections[name]"
              :name="name"
              :options="getOptions(name)"
              @detach="(detached) => detach(name, detached)"
              @updatePicked="(picked) => updatePicked(picked, name)"
              @update:data="(data) => $emit('update:data', data)"
              @update:relation="(data) => $emit('update:relation', data)"
              @trigger:refresh="reload()"
            ></resource-collection>
          </card>
          <div v-if="model.meta.belongsToMany[name]" class="row">
            <div class="col-12">
              <card
                :id="`resource-component_${resourceType}-${name}_selection`"
                :title="trans('models.' + resourceType + '.relations.' + name) + ' hinzufügen'"
                class="mb-2"
              >
                <resource-selection
                  :name="name"
                  :resource-type="model.meta.collections[name]"
                  :origin="model"
                  @update:attached="(attached) => attach(name, attached)"
                ></resource-selection>
              </card>
            </div>
          </div>
          <div v-else-if="model.meta.hasMany[name]" class="row">
            <div class="col-12">
              <picked-resource-card :name="name" :picked="picked[name]" :resource="model"></picked-resource-card>
            </div>
          </div>
        </template>
      </div>
    </div>
    <template #right>
      <admin-sidebox :resource="model"></admin-sidebox>
    </template>
  </Layout>
</template>

<script>
import Card from '@kanrisha/components/Base/Card';
import Layout from '@kanrisha/Pages/Layout';
import ResourceForm from '@kanrisha/components/ResourceForm';
import ResourceActionButtons from '@kanrisha/components/Links/ResourceActionButtons';
import ResourceCollection from '@kanrisha/components/ResourceCollection';
import ResourceSelection from '@kanrisha/components/ResourceSelection';
import useModels from '@kanrisha/composables/useModels';
import { ref } from 'vue';
import { router } from '@inertiajs/vue3';
import useResource from '@kanrisha/composables/useResource';
import useToasts from '@kanrisha/composables/useToasts';
import AdminSidebox from '@kanrisha/components/Layout/AdminSidebox';
import PickedResourceCard from '@kanrisha/components/Cards/PickedResource';
import RelationCollectionCard from '@kanrisha/components/Cards/RelationCollection';
import ResourceActionHeader from '@kanrisha/components/Layout/ResourceActionHeader';

export default {
  components: {
    AdminSidebox,
    Card,
    Layout,
    PickedResourceCard,
    RelationCollectionCard,
    ResourceActionButtons,
    ResourceActionHeader,
    ResourceCollection,
    ResourceForm,
    ResourceSelection,
  },
  props: {
    aspectMenuItems: { type: Object, default: () => {} },
    model: { type: Object, default: () => {} },
    logEntries: { type: Object, default: () => {} },
    toasts: { type: Object, default: () => {} },
    errors: { type: Object, default: () => {} },
  },
  emits: ['update:toast'],
  setup(props) {
    const { meta, resourceType } = useResource(props, props.model);
    const picked = ref(props.model.data.picked);
    const { addToasts } = useToasts(props);
    const focus = ref(props.model.meta.adjustments.focus ?? 'self');

    const hasDescription = (trans) => {
      let transString = `models.${props.resourceType}.content.edit.description`;
      let translation = trans(transString);

      return translation && translation !== transString;
    };

    const updatePicked = (pickedModel, name) => {
      picked.value[name].data = pickedModel.model.data;
    };

    const setFocus = (name) => {
      const { focus: setFocusAdjustment } = useModels({ resourceType: props.model.meta.resourceType });
      setFocusAdjustment(props.model.meta.resourceType, name);
      focus.value = name;
    };

    const attach = async (name, attached) => {
      const { attachModel } = useModels({ resourceType: props.model.meta.resourceType });
      await attachModel(props.model.data.id, props.model.meta.resourceType, name, attached.data.id);
      router.reload();
    };

    const detach = async (name, detached) => {
      const { detachModel } = useModels({ resourceType: props.model.meta.resourceType });
      await detachModel(props.model.data.id, props.model.meta.resourceType, name, detached.data.id);
      router.reload();
    };

    const reload = () => {
      router.reload();
    };

    const getOptions = (name) => {
      let options = {};
      options.relation = props.model.data.collections[name].meta.relationInfo;
      options.parentResource = props.model;

      return options;
    };

    return {
      addToasts,
      attach,
      detach,
      focus,
      getOptions,
      hasDescription,
      meta,
      picked,
      resourceType,
      setFocus,
      updatePicked,
    };
  },
};
</script>
